import { useEffect, useState } from "react";
import { MdDeleteForever } from "react-icons/md";
import Loader from "../../common/Loader";
import Pagination from "../../common/Pagination";
import axiosInstance from "../../api/axiosInstance.js";
import { notify, Toast } from "../../common/Toast";
import { getUserMenuPermission } from "../../common/Helper/index.js";
import formatDateIndian from "../../common/formatDateIndian.js";

const ActivityLog = () => {
  const permission = getUserMenuPermission(13);
  const [isActiveData, setIsActiveData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const userId = localStorage.getItem("userId");

  const fetchActivityData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `ActivityLog/GetAll?pageNumber=${currentPage}&pageSize=${pageSize}`
      );
      setIsActiveData(response.data.data);
      setTotalRecords(response.data.totalRecords);
      setTotalPages(Math.ceil(response.data.totalRecords / pageSize));
    } catch (error) {
      console.error("Error fetching activity data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`ActivityLog/Delete/${id}`);
      fetchActivityData();
      notify("error", "Menu deleted successfully");
    } catch (err) {
      console.error("Error deleting menu:", err);
    }
  };

  useEffect(() => {
    fetchActivityData();
  }, [currentPage]);

  return (
    <>
      <Toast />
      <div >
        <div className="rounded-3xl bg-transparent px-2 pt-2 pb-6">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 tracking-tight">System Audit Log</h2>
            <p className="text-sm text-gray-500 font-medium">Track all system activities, user actions, and security events</p>
          </div>

          <div className="premium-card overflow-hidden border-none shadow-xl shadow-gray-200/50">
            {loading ? (
              <div className="p-20 flex justify-center"><Loader /></div>
            ) : permission.isView ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[#f9fafc] text-[#405189] uppercase tracking-wider text-[11px] font-bold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">Event Type</th>
                      <th className="px-6 py-4">IP Address</th>
                      <th className="px-6 py-4">Activity Description</th>
                      <th className="px-6 py-4">Performed By</th>
                      <th className="px-6 py-4 text-center">Date</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {isActiveData?.length > 0 ? (
                      isActiveData.map((act) => (
                        <tr
                          key={act.id}
                          className="hover:bg-blue-50/30 transition-colors group cursor-pointer"
                        >
                          <td className="px-6 py-4">
                            <span className="px-2.5 py-1 bg-primary/5 text-primary rounded-lg text-[10px] font-bold uppercase tracking-wider">
                              {act.activity_Type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <code className="text-[11px] font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">
                              {act.ip_Address}
                            </code>
                          </td>
                          <td className="px-6 py-4 text-gray-600 font-medium max-w-md">
                            <p className="truncate" title={act.message}>{act.message}</p>
                          </td>
                          <td className="px-6 py-4 font-bold text-gray-800">
                            {act.user_Name}
                          </td>
                          <td className="px-6 py-4 text-center text-gray-500 text-[11px] font-bold">
                            {formatDateIndian(act.created_Date)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              {permission.isDelete && (
                                <button
                                  className="p-2 bg-gray-50 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(act.id); }}
                                  title="Remove Log Entry"
                                >
                                  <MdDeleteForever className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-20 text-gray-400 font-medium italic">
                          No audit records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-red-500 text-lg py-20 font-bold bg-white">
                Access Denied: You don't have permission to view audit logs.
              </div>
            )}

            <div className="bg-[#f9fafc] px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-[13px] text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">
                  {isActiveData?.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalRecords)}
                </span> of{" "}
                <span className="font-bold text-gray-800">{totalRecords}</span> records
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityLog;
